import { OpportunityStage, PersistentState, Opportunity } from "../types";
import { PanelGuard, GuardResult } from "../lib/panel";

export type WorkflowTransition = {
  from: OpportunityStage;
  to: OpportunityStage;
  label: string;
  guard?: (opportunity: Opportunity, state: PersistentState) => GuardResult;
};

/**
 * Formal Workflow State Machine 🤖
 *
 * Centralizes all stage transition logic and expert gating.
 * Eliminates "Magic Strings" and scattered if/else checks.
 */
export const WorkflowMachine = {
  transitions: [
    {
      from: "intake_started",
      to: "intake_complete",
      label: "Finish setup",
    },
    {
      from: "intake_complete",
      to: "fit_review",
      label: "Start fit check",
    },
    {
      from: "fit_review",
      to: "positioning",
      label: "Start positioning",
    },
    {
      from: "positioning",
      to: "outreach_ready",
      label: "Ready for outreach",
      guard: (opp, state) => PanelGuard.validateEvidenceDepth(opp, state as any),
    },
    {
      from: "outreach_ready",
      to: "interview_active",
      label: "Interview booked",
    },
    {
      from: "interview_active",
      to: "debrief_captured",
      label: "Record debrief",
    },
    {
      from: "debrief_captured",
      to: "offer_review",
      label: "Review offer",
    },
    {
      from: "offer_review",
      to: "closed_won",
      label: "Accept offer 🥂",
    },
  ] as WorkflowTransition[],

  /**
   * Gets the next valid transition for an opportunity.
   */
  getNextTransition(opportunity: Opportunity): WorkflowTransition | undefined {
    return this.transitions.find((t) => t.from === opportunity.current_stage);
  },

  /**
   * Validates if a move is architecturally sound.
   */
  canMove(opportunity: Opportunity, state: PersistentState): GuardResult {
    const transition = this.getNextTransition(opportunity);
    if (!transition) {
      return {
        passed: false,
        failures: ["No further moves available from this stage."],
        warnings: [],
        recommendations: [],
      };
    }

    if (transition.guard) {
      return transition.guard(opportunity, state);
    }

    return { passed: true, failures: [], warnings: [], recommendations: [] };
  },
};
